import { useMemo } from 'react'

import { FigureCard } from '@/components/ui'
import type { Transaction } from '@/modules/projects'

interface TransactionsStatsProps {
  transactions: Transaction[]
}
export const TransactionsStats = ({ transactions }: TransactionsStatsProps) => {
  const summaryStats = useMemo(() => {
    if (!transactions.length) {
      return { inflows: 0, outflows: 0, totalTransactions: 0 }
    }

    // Internal transfers (the customer's own cash moving between their buckets) are shown in the table
    // but net to zero, so they are left out of the money in / money out totals.
    const inflows = transactions.reduce(
      (sum: number, tx: Transaction) =>
        tx.amount > 0 && !tx.internalTransfer ? sum + tx.amount : sum,
      0,
    )
    const outflows = transactions.reduce(
      (sum: number, tx: Transaction) =>
        tx.amount < 0 && !tx.internalTransfer ? sum + Math.abs(tx.amount) : sum,
      0,
    )

    return {
      inflows,
      outflows,
      totalTransactions: transactions.length,
    }
  }, [transactions])

  return (
    // @container so the columns react to the available width (which shrinks when the chat panel
    // opens), not the viewport. One column until there's room for all three, skipping a two-column
    // step that would leave one card beside an empty slot.
    <div className="@container">
      <div className="grid grid-cols-1 @3xl:grid-cols-3 gap-4">
        <FigureCard
          label="Total inflows"
          value={`$${summaryStats.inflows.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`}
          valueClassName="text-emerald-500"
        />
        <FigureCard
          label="Total outflows"
          value={`$${summaryStats.outflows.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`}
          valueClassName="text-red-600"
        />
        <FigureCard
          label="Total transactions"
          value={summaryStats.totalTransactions.toLocaleString('en-US')}
        />
      </div>
    </div>
  )
}
